import React from 'react';
import { Button } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';

/**
 * QuickDatePresets
 *
 * A small row of chip buttons (1D, 2D, 1W, 2W, 1M, 2M) that set a study-date
 * range without the user having to pick start/end dates manually.
 *
 * It is purely additive: it only calls `onChange` with a [start, end] dayjs
 * tuple, reusing whatever `setDateRange` the host page already has. The
 * existing RangePicker and filter pipeline are untouched.
 */

export type DateRangeValue = [Dayjs | null, Dayjs | null] | null;

interface PresetDef {
  label: string;
  count: number;
  unit: 'day' | 'week' | 'month';
}

// Rolling windows that always end at the end of today.
// Day windows are inclusive of today (1D = today only, 2D = today + yesterday).
const PRESETS: PresetDef[] = [
  { label: '1D', count: 1, unit: 'day' },
  { label: '2D', count: 2, unit: 'day' },
  { label: '1W', count: 1, unit: 'week' },
  { label: '2W', count: 2, unit: 'week' },
  { label: '1M', count: 1, unit: 'month' },
  { label: '2M', count: 2, unit: 'month' },
];

export const getPresetRange = (preset: PresetDef): [Dayjs, Dayjs] => {
  const end = dayjs().hour(23).minute(59).second(59);
  const start =
    preset.unit === 'day'
      ? dayjs().subtract(preset.count - 1, 'day').hour(0).minute(0).second(0) // inclusive of today
      : dayjs().subtract(preset.count, preset.unit).hour(0).minute(0).second(0);
  return [start, end];
};

const fmt = (d: Dayjs) => d.format('YYYY-MM-DD HH:mm');

const rangeMatchesPreset = (value: DateRangeValue, preset: PresetDef): boolean => {
  if (!value || !value[0] || !value[1]) {
    return false;
  }
  const [start, end] = getPresetRange(preset);
  return fmt(value[0]) === fmt(start) && fmt(value[1]) === fmt(end);
};

interface Props {
  value: DateRangeValue;
  onChange: (range: [Dayjs, Dayjs]) => void;
  className?: string;
}

const QuickDatePresets: React.FC<Props> = ({ value, onChange, className }) => {
  return (
    <div
      className={`quick-date-presets${className ? ` ${className}` : ''}`}
      style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}
    >
      {PRESETS.map(preset => {
        const active = rangeMatchesPreset(value, preset);
        return (
          <Button
            key={preset.label}
            size="small"
            type={active ? 'primary' : 'default'}
            onClick={() => onChange(getPresetRange(preset))}
          >
            {preset.label}
          </Button>
        );
      })}
    </div>
  );
};

export default QuickDatePresets;
